import { useCallback, useMemo } from "react";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { useApplicationContext } from "src/hooks/useApplication";
import { ToasterMessageType, useToasterContext } from "src/hooks/useToaster";
import { SpaceItem, SpaceItemType } from "pages/api/[orgId]/spaces/types";
import * as SpaceClient from "pages/api-clients/spaces";
import { useDeepLinks } from "src/hooks/useDeepLinks";
import { DeepLinkType } from "pages/api/deep-links/types";

/*********************************
 * Use for interacting with spaces
 * - Provide a space id for access to the data and mutators for it
 * - Can use {disabled:true} to prevent any ajax
 ***************************************************/

// useful for prop types in components
export type UseSpacesQuery = ReturnType<typeof useSpacesQuery>;
export type SpacesData = Pick<UseSpacesQuery, "spaces">["spaces"];
export type SpaceData = Exclude<
  Pick<UseSpacesQuery, "space">["space"],
  undefined
>;
export type UseSpacesQueryWithSpace = UseSpacesQuery & { space: SpaceData };
export type UpsertSpace = Parameters<
  UseSpacesQuery["upsertSpace"]["mutate"]
>[0];

// Main hook
export function useSpacesQuery(spaceId = "", config?: { disabled?: boolean }) {
  // Dependencies: identity and messaging
  const app = useApplicationContext();
  const toaster = useToasterContext();

  // Small detail, but helps with determining which components to render
  const ready = app.ready;

  // We'll use the keys and client to invalidate and refetch after mutations
  const orgId = app.org_id!;
  const client = useQueryClient();
  const spacesQueryKey = ["spaces", { orgId }] as const;
  const spaceQueryKey = ["space", { orgId, spaceId }] as const;

  // Ensures the hook only runs when the expected data is present
  const spacesEnabled = Boolean(!config?.disabled && ready && orgId);
  const spaceEnabled = Boolean(spacesEnabled && spaceId);

  // The two react queries, one for spaces and one for a space if needed
  const spacesQuery = useQuery(
    ["spaces", { orgId }],
    async () => SpaceClient.listSpaces(orgId, { include: "items" }),
    { enabled: spacesEnabled }
  );
  const spaceQuery = useQuery(
    spaceQueryKey,
    async () => SpaceClient.readSpace(orgId, { uuid: spaceId }),
    { enabled: spaceEnabled }
  );

  // We'll return some nicely named variables for the data itself
  const spaces = useMemo(() => spacesQuery.data || [], [spacesQuery.data]);
  const space = useMemo(() => spaceQuery.data || undefined, [spaceQuery.data]);

  // Gotta take care of permissions. We'll provide a callback for items
  const spaceIsGlobal = Boolean(space && !space.user_id && !space.org_id);
  const spaceIsOrg = Boolean(space && space.org_id && !space.user_id);
  const spaceIsPrivate = Boolean(space && !spaceIsGlobal && !spaceIsOrg);
  const userIsSuper = app.isSuperAdmin;
  const userIsAdmin = app.isOrgAdmin;
  const canModifySpace =
    (spaceIsGlobal && userIsSuper) ||
    (spaceIsOrg && userIsAdmin) ||
    spaceIsPrivate;

  const canModifySpaceItem = useCallback(
    (item: SpaceItem) => {
      const itemIsGlobal = Boolean(item.org_id);
      const itemIsOrg = Boolean(item.user_id);
      const itemIsPrivate = !itemIsGlobal && !itemIsOrg;
      return (
        (itemIsGlobal && userIsSuper) ||
        (itemIsOrg && userIsAdmin) ||
        itemIsPrivate
      );
    },
    [userIsAdmin, userIsSuper]
  );

  // Finally all the mutators. Each is sure to invalidate any cache
  // or set any cache data as needed, then also displaying a message.
  const upsertSpace = useMutation({
    mutationFn: async (data: {
      space_id?: string;
      name: string;
      description?: string;
      color?: string;
      pattern?: string;
      tags?: string[];
      scope: "global" | "org" | "private";
    }) =>
      data.space_id // weird spread for TS inference engine
        ? SpaceClient.updateSpace(orgId, { ...data, space_id: data.space_id })
        : SpaceClient.createSpace(orgId, data),
    onSuccess: (data, { space_id }) => {
      if (space_id)
        client.setQueryData(
          [spaceQueryKey[0], { ...spaceQueryKey[1], spaceId: space_id }],
          data
        );
      spacesQuery.refetch();
      toaster.addMessage(ToasterMessageType.SUCCESS, {
        message: `${space_id ? "Saved" : "Created"} space`,
      });
    },
  });

  const deleteSpace = useMutation({
    mutationFn: async (data: { space_id: string }) =>
      SpaceClient.deleteSpace(orgId, data),
    onSuccess: () => {
      spacesQuery.refetch();
      toaster.addMessage(ToasterMessageType.SUCCESS, {
        message: "Removed space",
      });
    },
  });

  const pinSpace = useMutation({
    mutationFn: async (fields: {
      space_id: string;
      action: "add" | "remove";
    }) => SpaceClient.pinSpaceToNav(orgId, fields),
    onSuccess: async (data, { action }) => {
      client.setQueryData(spaceQueryKey, data);
      client.invalidateQueries({ queryKey: spacesQueryKey });
      toaster.addMessage(ToasterMessageType.SUCCESS, {
        message:
          action === "add"
            ? "Added space to favorites"
            : "Removed space from favorites",
      });
    },
  });

  const createSpaceItem = useMutation({
    mutationFn: async (fields: {
      type: SpaceItemType;
      space_id: string;
      name: string;
      description?: string;
      scope?: "global" | "org" | "private";
      deep_link_id?: string;
      entity_uuid?: string;
    }) => SpaceClient.createSpaceItem(orgId, fields),
    onSuccess: async (data) => {
      client.setQueryData(spaceQueryKey, data);
      client.invalidateQueries({ queryKey: spacesQueryKey });
      toaster.addMessage(ToasterMessageType.SUCCESS, {
        message: "Added item to space",
      });
    },
  });

  const updateSpaceItem = useMutation({
    mutationFn: async (fields: {
      space_id: string;
      item_id: string;
      name: string;
      description?: string;
      scope: "global" | "org" | "private";
    }) => SpaceClient.updateSpaceItem(orgId, fields),
    onSuccess: async (data) => {
      client.setQueryData(spaceQueryKey, data);
      client.invalidateQueries({ queryKey: spacesQueryKey });
      toaster.addMessage(ToasterMessageType.SUCCESS, {
        message: "Saved item",
      });
    },
  });

  const deleteSpaceItem = useMutation({
    mutationFn: async (fields: { space_id: string; item_id: string }) =>
      SpaceClient.deleteSpaceItem(orgId, fields),
    onSuccess: async (data) => {
      client.setQueryData(spaceQueryKey, data);
      client.invalidateQueries({ queryKey: spacesQueryKey });
      toaster.addMessage(ToasterMessageType.SUCCESS, {
        message: "Removed item from space",
      });
    },
  });

  return {
    canModifySpace,
    canModifySpaceItem,
    createSpaceItem,
    deleteSpace,
    deleteSpaceItem,
    pinSpace,
    ready,
    space,
    spaceQuery,
    spaces,
    spacesQuery,
    updateSpaceItem,
    upsertSpace,
  };
} // end main hook

type UnannotatedSpace = ReturnType<typeof useSpacesQuery>["spaces"][number];
type AnnotatedSpace = UnannotatedSpace & {
  useCases: string[];
  targetRoles: string[];
};

export function useSpaceAnnotations(
  spaces: UnannotatedSpace[]
): AnnotatedSpace[] {
  const boards = useDeepLinks(DeepLinkType.BOARD);
  const queries = useDeepLinks(DeepLinkType.EXPLORE);
  const linksList = [
    ...(boards.links.data || []),
    ...(queries.links.data || []),
  ];
  const linksTable = Object.fromEntries(
    linksList.map((link) => [
      link.id,
      { useCase: link.useCase || "", targetRole: link.targetRole || "" },
    ])
  );
  return useMemo(() => {
    return spaces.map((space) => {
      const targetRoles = space.item_ids
        .map((id) => linksTable[id]?.targetRole)
        .filter(Boolean);
      const useCases = space.item_ids
        .map((id) => linksTable[id]?.useCase)
        .filter(Boolean);
      return { ...space, useCases, targetRoles };
    });
  }, [linksTable, spaces]);
}
